import React, { useState, useEffect, memo, useCallback, useMemo, createContext, useContext } from 'react';
import { Layout, Card, Avatar, Typography, Button, Space, Tag, Input, Badge, Tooltip, Modal, notification, Spin, Statistic, Descriptions, Select, DatePicker, Drawer, Tabs, Progress, Empty, Divider, Skeleton, Result, FloatButton } from 'antd';
import { UserOutlined, LogoutOutlined, BellOutlined, FileTextOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, SyncOutlined, FileOutlined, HomeOutlined, TeamOutlined, SafetyOutlined, EyeOutlined, FilterOutlined, SortAscendingOutlined, DownloadOutlined, CalendarOutlined, EnvironmentOutlined, SearchOutlined, SettingOutlined, DashboardOutlined, RiseOutlined, AlertOutlined, StarOutlined } from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { QRCodeCanvas } from 'qrcode.react';
import useQrUserData from './useQrUserData';
dayjs.extend(relativeTime);
const { Header, Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Design System
const designSystem = {
  colors: {
    primary: '#1677ff',
    secondary: '#722ed1',
    success: '#52c41a',
    warning: '#faad14',
    error: '#ff4d4f',
    info: '#13c2c2',
    neutral: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#f0f0f0',
      300: '#d9d9d9',
      400: '#bfbfbf',
      500: '#8c8c8c',
      600: '#595959',
      700: '#434343',
      800: '#262626',
      900: '#1f1f1f'
    },
    gradient: {
      primary: 'linear-gradient(135deg, #1677ff 0%, #722ed1 100%)',
      success: 'linear-gradient(135deg, #52c41a 0%, #13c2c2 100%)',
      warning: 'linear-gradient(135deg, #faad14 0%, #ff7a45 100%)'
    }
  },
  shadows: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    glow: '0 0 20px rgba(22, 119, 255, 0.3)'
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px'
  },
  borderRadius: {
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '50%'
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: {
      xs: '12px',
      sm: '14px',
      md: '16px',
      lg: '18px',
      xl: '20px',
      xxl: '24px'
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    }
  }
};

// Animations
const animations = {
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3 }
  },
  slideUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
    transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] }
  },
  slideInLeft: {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
    transition: { duration: 0.3 }
  },
  scale: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    transition: { duration: 0.3 }
  },
  stagger: {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  }
};

// Composant ModernHeader
const ModernHeader = memo(({ nhGuideNumber }) => {
  const navigate = useNavigate();
  const { userData, loading } = useQrUserData(nhGuideNumber);
  const [notificationDrawer, setNotificationDrawer] = useState(false);
  const handleLogout = useCallback(async () => {
    try {
      const response = await fetch('/backend/logout.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (response.ok) {
        notification.success({
          message: 'Logout Successful',
          description: 'You have been successfully logged out.',
          placement: 'topRight',
        });
        navigate('/login');
      }
    } catch (error) {
      notification.error({
        message: 'Logout Error',
        description: 'An error occurred during logout',
        placement: 'topRight',
      });
    }
  }, [navigate]);
  const navItems = [];
});

// Composant StatCard
const StatCard = memo(({ icon, title, value, description, color, trend, loading, onClick }) => (
  <motion.div
    style={{
      background: 'white',
      borderRadius: designSystem.borderRadius.xl,
      padding: designSystem.spacing.lg,
      border: `1px solid ${designSystem.colors.neutral[200]}`,
      boxShadow: designSystem.shadows.sm,
      cursor: onClick ? 'pointer' : 'default',
      position: 'relative',
      overflow: 'hidden'
    }}
    whileHover={{
      y: -4,
      boxShadow: designSystem.shadows.lg,
      transition: { duration: 0.2 }
    }}
    onClick={onClick}
    {...animations.scale}
  >
    <div style={{
      position: 'absolute',
      top: 0,
      right: 0,
      width: '100px',
      height: '100px',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      borderRadius: '50%',
      transform: 'translate(30px, -30px)'
    }} />
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: designSystem.spacing.md,
      position: 'relative',
      zIndex: 1
    }}>
      <div style={{
        width: '48px',
        height: '48px',
        borderRadius: designSystem.borderRadius.lg,
        background: `${color}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        fontSize: '20px',
        boxShadow: `0 4px 12px ${color}25`
      }}>
        {icon}
      </div>
      {trend && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 8px',
          borderRadius: designSystem.borderRadius.sm,
          background: trend > 0 ? `${designSystem.colors.success}15` : `${designSystem.colors.error}15`,
          color: trend > 0 ? designSystem.colors.success : designSystem.colors.error,
          fontSize: designSystem.typography.fontSize.xs,
          fontWeight: designSystem.typography.fontWeight.semibold
        }}>
          <RiseOutlined style={{
            transform: trend < 0 ? 'rotate(180deg)' : 'none',
            fontSize: '12px'
          }} />
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    {loading ? (
      <Skeleton active paragraph={{ rows: 2 }} />
    ) : (
      <>
        <Statistic
          title={title}
          value={value}
          valueStyle={{
            color: designSystem.colors.neutral[800],
            fontSize: '28px',
            fontWeight: designSystem.typography.fontWeight.bold,
            lineHeight: 1.2
          }}
          style={{ marginBottom: designSystem.spacing.sm }}
        />
        <Text style={{
          color: designSystem.colors.neutral[500],
          fontSize: designSystem.typography.fontSize.sm,
          lineHeight: 1.4
        }}>
          {description}
        </Text>
      </>
    )}
  </motion.div>
));

// Composant ModernHistoryCard
const ModernHistoryCard = memo(({ record, type, onView, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const getStatusConfig = (status) => {
    const configs = {
      'active': { color: designSystem.colors.success, text: 'Active', bg: `${designSystem.colors.success}15` },
      'pending': { color: designSystem.colors.warning, text: 'Pending', bg: `${designSystem.colors.warning}15` },
      'completed': { color: designSystem.colors.info, text: 'Completed', bg: `${designSystem.colors.info}15` },
      'urgent': { color: designSystem.colors.error, text: 'Urgent', bg: `${designSystem.colors.error}15` },
      'approved': { color: designSystem.colors.success, text: 'Approved', bg: `${designSystem.colors.success}15` },
      'on-hold': { color: designSystem.colors.neutral[500], text: 'On Hold', bg: `${designSystem.colors.neutral[500]}15` }
    };
    return configs[status?.toLowerCase()] || configs.pending;
  };
  const statusConfig = getStatusConfig(record.status);
  const isSupport = type === 'support';
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
      style={{
        background: 'white',
        borderRadius: designSystem.borderRadius.xl,
        border: `1px solid ${designSystem.colors.neutral[200]}`,
        boxShadow: isHovered ? designSystem.shadows.lg : designSystem.shadows.sm,
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        position: 'relative'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ y: -2 }}
    >
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: statusConfig.color
      }} />
      <div style={{ padding: designSystem.spacing.lg }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: designSystem.spacing.md
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: designSystem.spacing.md }}>
            <Avatar
              icon={<FileOutlined />}
              style={{
                backgroundColor: statusConfig.bg,
                color: statusConfig.color,
                border: `2px solid ${statusConfig.color}30`
              }}
              size={48}
            />
            <div>
              <Title level={5} style={{
                margin: 0,
                color: designSystem.colors.neutral[800],
                fontSize: designSystem.typography.fontSize.md,
                fontWeight: designSystem.typography.fontWeight.semibold
              }}>
                {isSupport
                  ? `Support #${record.support_number || 'N/A'}`
                  : `${record.activity_type || 'Activity'}`
                }
              </Title>
              <Space size={8} style={{ marginTop: '4px' }}>
                <Tag
                  color={statusConfig.color}
                  style={{
                    borderRadius: designSystem.borderRadius.sm,
                    border: 'none',
                    background: statusConfig.bg,
                    color: statusConfig.color,
                    fontWeight: designSystem.typography.fontWeight.medium
                  }}
                >
                  {statusConfig.text}
                </Tag>
                {isSupport && (
                  <Text style={{
                    fontSize: designSystem.typography.fontSize.xs,
                    color: designSystem.colors.neutral[500]
                  }}>
                    {record.record_type || 'N/A'}
                  </Text>
                )}
              </Space>
            </div>
          </div>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onView(record, type)}
              style={{
                borderRadius: designSystem.borderRadius.full,
                color: designSystem.colors.primary,
                background: `${designSystem.colors.primary}10`
              }}
            />
          </Tooltip>
        </div>
        {isSupport ? (
          <div style={{ marginTop: designSystem.spacing.md }}>
            {record.needs?.length > 0 && (
              <div style={{ marginBottom: designSystem.spacing.sm }}>
                <Text style={{
                  fontSize: designSystem.typography.fontSize.xs,
                  color: designSystem.colors.neutral[600],
                  fontWeight: designSystem.typography.fontWeight.medium,
                  display: 'block',
                  marginBottom: '4px'
                }}>
                  Needs:
                </Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {record.needs.slice(0, 3).map((need, idx) => (
                    <Tag
                      key={idx}
                      size="small"
                      style={{
                        background: `${designSystem.colors.info}10`,
                        color: designSystem.colors.info,
                        border: `1px solid ${designSystem.colors.info}20`,
                        borderRadius: designSystem.borderRadius.sm
                      }}
                    >
                      {need}
                    </Tag>
                  ))}
                  {record.needs.length > 3 && (
                    <Tag size="small" style={{ background: designSystem.colors.neutral[100] }}>
                      +{record.needs.length - 3} more
                    </Tag>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginTop: designSystem.spacing.md }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text style={{
                  fontSize: designSystem.typography.fontSize.xs,
                  color: designSystem.colors.neutral[500]
                }}>
                  Provider:
                </Text>
                <Text style={{
                  fontSize: designSystem.typography.fontSize.xs,
                  color: designSystem.colors.neutral[700],
                  fontWeight: designSystem.typography.fontWeight.medium
                }}>
                  {record.service_provider || 'N/A'}
                </Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text style={{
                  fontSize: designSystem.typography.fontSize.xs,
                  color: designSystem.colors.neutral[500]
                }}>
                  Date:
                </Text>
                <Text style={{
                  fontSize: designSystem.typography.fontSize.xs,
                  color: designSystem.colors.neutral[700],
                  fontWeight: designSystem.typography.fontWeight.medium
                }}>
                  {record.activity_date ? dayjs(record.activity_date).format('MMM DD, YYYY') : 'N/A'}
                </Text>
              </div>
              {record.documents?.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text style={{
                    fontSize: designSystem.typography.fontSize.xs,
                    color: designSystem.colors.neutral[500]
                  }}>
                    Documents:
                  </Text>
                  <Badge
                    count={record.documents.length}
                    style={{
                      backgroundColor: designSystem.colors.info,
                      fontSize: '10px'
                    }}
                  />
                </div>
              )}
            </Space>
          </div>
        )}
        <Divider style={{ margin: `${designSystem.spacing.md} 0 ${designSystem.spacing.sm} 0` }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text style={{
            fontSize: designSystem.typography.fontSize.xs,
            color: designSystem.colors.neutral[400]
          }}>
            {record.created_at ? dayjs(record.created_at).fromNow() : 'Recently'}
          </Text>
          {isSupport && (
            <Text style={{
              fontSize: designSystem.typography.fontSize.xs,
              color: designSystem.colors.neutral[400]
            }}>
              ID: {record.key || 'N/A'}
            </Text>
          )}
        </div>
      </div>
    </motion.div>
  );
});

// Composant principal UserqrSupportCases
const UserqrSupportCases = () => {
  const [searchParams] = useSearchParams();
  const nhGuideNumber = searchParams.get('nhGuideNumber');
  const navigate = useNavigate();
  const { userData, loading: userLoading, error: userError } = useQrUserData(nhGuideNumber);
  const [supportHistory, setSupportHistory] = useState([]);
  const [activityHistory, setActivityHistory] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [sortBy, setSortBy] = useState('date_desc');
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedRecordType, setSelectedRecordType] = useState(null);
  const [activeTab, setActiveTab] = useState('support');

  // Récupérer l'historique des supports
  const fetchSupportHistory = useCallback(async () => {
    if (!userData?.userEmail) return;
    try {
      const response = await fetch(
        `/backend/user_get_support_history.php?user_email=${encodeURIComponent(userData.userEmail)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (jsonError) {
        throw new Error('Invalid JSON response from server');
      }
      if (response.ok) {
        const parsedData = data.map((record) => ({
          ...record,
          needs: Array.isArray(record.needs)
            ? record.needs
            : typeof record.needs === 'string'
            ? record.needs.startsWith('[')
              ? JSON.parse(record.needs)
              : [record.needs]
            : [],
          connections: Array.isArray(record.connections)
            ? record.connections
            : typeof record.connections === 'string'
            ? record.connections.startsWith('[')
              ? JSON.parse(record.connections)
              : [record.connections]
            : [],
        }));
        setSupportHistory(parsedData);
      } else {
        throw new Error(data.error || 'Failed to load support history');
      }
    } catch (error) {
      console.error('Error loading support history:', error);
      notification.error({
        message: 'Loading Error',
        description: error.message || 'Unable to load support history',
        placement: 'topRight',
      });
    }
  }, [userData?.userEmail]);

  // Récupérer l'historique des activités
  const fetchActivityHistory = useCallback(async () => {
    if (!userData?.userEmail) return;
    try {
      const response = await fetch(
        `/backend/user_get_service_activities.php?user_email=${encodeURIComponent(userData.userEmail)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (jsonError) {
        throw new Error('Invalid JSON response from server');
      }
      if (response.ok) {
        setActivityHistory(data.activities || []);
      } else {
        throw new Error(data.error || 'Failed to load activity history');
      }
    } catch (error) {
      console.error('Error loading activity history:', error);
      notification.error({
        message: 'Loading Error',
        description: error.message || 'Unable to load activity history',
        placement: 'topRight',
      });
    }
  }, [userData?.userEmail]);

  // Récupérer les documents
  const fetchDocuments = useCallback(async () => {
    if (!userData?.userEmail) return;
    try {
      const response = await fetch(
        `/backend/user_get_documents.php?user_email=${encodeURIComponent(userData.userEmail)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (jsonError) {
        throw new Error('Invalid JSON response from server');
      }
      if (response.ok) {
        setDocuments(data || []);
      } else {
        throw new Error(data.error || 'Failed to load documents');
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      notification.error({
        message: 'Loading Error',
        description: error.message || 'Unable to load documents',
        placement: 'topRight',
      });
    }
  }, [userData?.userEmail]);

  // Charger les données dès que userData.userEmail est disponible
  useEffect(() => {
    if (!userData?.userEmail) return;
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchSupportHistory(),
          fetchActivityHistory(),
          fetchDocuments(),
        ]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchSupportHistory, fetchActivityHistory, fetchDocuments, userData?.userEmail]);

  // Filtrer et trier les données
  const filteredAndSortedData = useMemo(() => {
    const filterData = (data, type) => {
      let filtered = data.filter((record) => {
        const searchFields = type === 'support'
          ? [record.support_number, record.record_type, record.status, ...(record.needs || []), ...(record.connections || [])]
          : [record.activity_type, record.service_provider, record.location];
        const matchesSearch = !searchTerm || searchFields.some((field) =>
          field?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
        const matchesStatus = !statusFilter || record.status?.toLowerCase() === statusFilter.toLowerCase();
        const matchesDate = !dateRange || (
          record.created_at &&
          dayjs(record.created_at).isAfter(dateRange[0]) &&
          dayjs(record.created_at).isBefore(dateRange[1])
        );
        return matchesSearch && matchesStatus && matchesDate;
      });
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'date_desc':
            return dayjs(b.created_at || b.activity_date).valueOf() - dayjs(a.created_at || a.activity_date).valueOf();
          case 'date_asc':
            return dayjs(a.created_at || a.activity_date).valueOf() - dayjs(b.created_at || b.activity_date).valueOf();
          case 'status':
            return (a.status || '').localeCompare(b.status || '');
          case 'type':
            return (a.record_type || a.activity_type || '').localeCompare(b.record_type || b.activity_type || '');
          default:
            return 0;
        }
      });
      return filtered;
    };
    return {
      support: filterData(supportHistory, 'support'),
      activity: filterData(activityHistory, 'activity'),
    };
  }, [supportHistory, activityHistory, searchTerm, statusFilter, dateRange, sortBy]);

  // Gérer l'affichage des détails d'un enregistrement
  const handleViewRecord = useCallback((record, type) => {
    setSelectedRecord(record);
    setSelectedRecordType(type);
    setViewModalVisible(true);
  }, []);

  // Rafraîchir les données
  const handleRefresh = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSupportHistory(),
        fetchActivityHistory(),
        fetchDocuments(),
      ]);
      notification.success({
        message: 'Data Refreshed',
        description: 'All data has been successfully updated',
        placement: 'topRight',
      });
    } catch (error) {
      notification.error({
        message: 'Refresh Failed',
        description: 'Unable to refresh data. Please try again.',
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
    }
  }, [fetchSupportHistory, fetchActivityHistory, fetchDocuments]);

  // Réinitialiser les filtres
  const handleResetFilters = useCallback(() => {
    setSearchTerm('');
    setStatusFilter(null);
    setDateRange(null);
    setSortBy('date_desc');
  }, []);

  // Afficher un état vide
  const renderEmptyState = (type) => (
    <motion.div
      style={{
        textAlign: 'center',
        padding: designSystem.spacing.xxl,
        background: designSystem.colors.neutral[50],
        borderRadius: designSystem.borderRadius.xl,
        border: `1px solid ${designSystem.colors.neutral[200]}`,
      }}
      {...animations.scale}
    >
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: designSystem.borderRadius.full,
        background: designSystem.colors.gradient.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '32px',
        margin: '0 auto 24px',
        boxShadow: designSystem.shadows.lg
      }}>
        <FileOutlined />
      </div>
      <Title level={3} style={{
        color: designSystem.colors.neutral[600],
        marginBottom: designSystem.spacing.sm
      }}>
        No {type} records found
      </Title>
      <Text style={{
        color: designSystem.colors.neutral[500],
        fontSize: designSystem.typography.fontSize.md
      }}>
        {searchTerm || statusFilter || dateRange
          ? 'Try adjusting your filters to see more results'
          : `No ${type} records have been created yet`}
      </Text>
    </motion.div>
  );

  // Afficher la liste des enregistrements
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;
  const renderRecordsList = (records, type) => (
    <motion.div
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))',
        gap: isMobile ? designSystem.spacing.sm : designSystem.spacing.lg,
        width: '100%',
      }}
      variants={animations.stagger}
      initial="initial"
      animate="animate"
    >
      {records.map((record, index) => (
        <ModernHistoryCard
          key={record.key || record.id || index}
          record={record}
          type={type}
          onView={handleViewRecord}
          index={index}
          isMobile={isMobile}
        />
      ))}
    </motion.div>
  );

  // Afficher une erreur si nhGuideNumber est manquant
  if (!nhGuideNumber) {
    return <Result status="error" title="No User Specified" subTitle="Please scan a valid QR code to view user data." />;
  }

  // Afficher une erreur si userError existe
  if (userError) {
    return <Result status="error" title="Error Loading User Data" subTitle={userError} />;
  }

  // Rendu du composant
  return (
    <Layout style={{ minHeight: '100vh', background: designSystem.colors.neutral[50] }}>
      <ModernHeader nhGuideNumber={nhGuideNumber} />
      <Content style={{
        padding: isMobile ? `${designSystem.spacing.md} ${designSystem.spacing.xs}` : `${designSystem.spacing.xl} ${designSystem.spacing.xl}`,
        maxWidth: '1400px',
        margin: '0 auto',
        width: '100%',
      }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* En-tête de la page */}
          <motion.div
            style={{
              background: 'white',
              borderRadius: designSystem.borderRadius.xl,
              padding: isMobile ? designSystem.spacing.md : designSystem.spacing.xl,
              marginBottom: isMobile ? designSystem.spacing.md : designSystem.spacing.xl,
              border: `1px solid ${designSystem.colors.neutral[200]}`,
              boxShadow: designSystem.shadows.sm,
              position: 'relative',
              overflow: 'hidden',
              flexDirection: isMobile ? 'column' : undefined,
            }}
            {...animations.slideUp}
          >
            <div style={{
              position: 'absolute',
              top: isMobile ? '-30px' : '-50px',
              right: isMobile ? '-30px' : '-50px',
              width: isMobile ? '120px' : '200px',
              height: isMobile ? '120px' : '200px',
              background: designSystem.colors.gradient.primary,
              borderRadius: '50%',
              opacity: 0.1,
            }} />
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
              zIndex: 1,
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? designSystem.spacing.sm : undefined,
            }}>
              {userLoading ? (
                <Skeleton avatar paragraph={{ rows: 1 }} />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? designSystem.spacing.sm : designSystem.spacing.md }}>
                  <Avatar
                    src="components/nhguidelogo.jpg"
                    icon={<UserOutlined />}
                    size={isMobile ? 40 : 64}
                    style={{
                      background: designSystem.colors.gradient.primary,
                      boxShadow: designSystem.shadows.sm,
                    }}
                  />
                  <div>
                    <Text style={{
                      color: designSystem.colors.neutral[500],
                      fontSize: isMobile ? designSystem.typography.fontSize.sm : designSystem.typography.fontSize.md,
                    }}>
                      NH Guide Number: {nhGuideNumber}
                    </Text>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
          {/* Statistiques */}
          {/* Filtres */}
          <motion.div
            style={{
              background: 'white',
              borderRadius: designSystem.borderRadius.xl,
              border: `1px solid ${designSystem.colors.neutral[200]}`,
              boxShadow: designSystem.shadows.sm,
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
            {...animations.slideUp}
            transition={{ delay: 0.3 }}
          >
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              size={isMobile ? 'small' : 'large'}
              style={{ margin: 0, overflowX: isMobile ? 'auto' : undefined }}
              items={[
                {
                  key: 'support',
                  label: (
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: designSystem.typography.fontSize.md,
                      fontWeight: designSystem.typography.fontWeight.medium,
                    }}>
                      <FileTextOutlined />
                      Support History
                      <Badge
                        count={filteredAndSortedData.support.length}
                        style={{ backgroundColor: designSystem.colors.primary }}
                      />
                    </span>
                  ),
                  children: (
                    <div style={{ padding: designSystem.spacing.xl }}>
                      {loading || userLoading ? (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                          gap: designSystem.spacing.lg,
                        }}>
                          {[...Array(6)].map((_, i) => (
                            <Card key={i} style={{ borderRadius: designSystem.borderRadius.xl }}>
                              <Skeleton active avatar paragraph={{ rows: 3 }} />
                            </Card>
                          ))}
                        </div>
                      ) : filteredAndSortedData.support.length > 0 ? (
                        renderRecordsList(filteredAndSortedData.support, 'support')
                      ) : (
                        renderEmptyState('support')
                      )}
                    </div>
                  ),
                },
                {
                  key: 'activity',
                  label: (
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: designSystem.typography.fontSize.md,
                      fontWeight: designSystem.typography.fontWeight.medium,
                    }}>
                      <DashboardOutlined />
                      Activity History
                      <Badge
                        count={filteredAndSortedData.activity.length}
                        style={{ backgroundColor: designSystem.colors.info }}
                      />
                    </span>
                  ),
                  children: (
                    <div style={{ padding: designSystem.spacing.xl }}>
                      {loading || userLoading ? (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                          gap: designSystem.spacing.lg,
                        }}>
                          {[...Array(6)].map((_, i) => (
                            <Card key={i} style={{ borderRadius: designSystem.borderRadius.xl }}>
                              <Skeleton active avatar paragraph={{ rows: 3 }} />
                            </Card>
                          ))}
                        </div>
                      ) : filteredAndSortedData.activity.length > 0 ? (
                        renderRecordsList(filteredAndSortedData.activity, 'activity')
                      ) : (
                        renderEmptyState('activity')
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </motion.div>
        </motion.div>
      </Content>
      {/* Modal pour afficher les détails */}
      <Modal
        title={
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: designSystem.spacing.md,
            padding: `${designSystem.spacing.md} 0`,
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: designSystem.borderRadius.lg,
              background: designSystem.colors.gradient.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}>
              <FileTextOutlined />
            </div>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {selectedRecordType === 'support' ? 'Support Record Details' : 'Activity Details'}
              </Title>
              <Text style={{ color: designSystem.colors.neutral[500], fontSize: designSystem.typography.fontSize.sm }}>
                Detailed information and documentation
              </Text>
            </div>
          </div>
        }
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setViewModalVisible(false)}
            style={{ borderRadius: designSystem.borderRadius.md }}
          >
            Close
          </Button>,
        ]}
        width={700}
        style={{ top: 20 }}
        styles={{
          content: { borderRadius: designSystem.borderRadius.xl },
          header: { borderBottom: `1px solid ${designSystem.colors.neutral[200]}` },
        }}
      >
        {selectedRecord && (
          <div style={{ padding: `${designSystem.spacing.lg} 0` }}>
            {selectedRecordType === 'support' ? (
              <Descriptions
                column={1}
                bordered
                size="middle"
                style={{ background: 'white' }}
                labelStyle={{
                  background: designSystem.colors.neutral[50],
                  fontWeight: designSystem.typography.fontWeight.semibold,
                  color: designSystem.colors.neutral[700],
                }}
              >
                <Descriptions.Item label="Support Number">
                  <Text copyable>{selectedRecord.support_number || 'N/A'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Record Type">
                  <Tag color="blue">{selectedRecord.record_type || 'N/A'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={
                    selectedRecord.status?.toLowerCase() === 'pending' ?
                    'warning' :
                    selectedRecord.status?.toLowerCase() === 'approved' ?
                    'success' : 'default'
                  }>
                    {selectedRecord.status || 'N/A'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Needs">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {selectedRecord.needs?.length > 0 ?
                      selectedRecord.needs.map((need, idx) => (
                        <Tag key={idx} color="blue">{need}</Tag>
                      )) :
                      <Text type="secondary">None specified</Text>
                    }
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Connections">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {selectedRecord.connections?.length > 0 ?
                      selectedRecord.connections.map((conn, idx) => (
                        <Tag key={idx} color="green">{conn}</Tag>
                      )) :
                      <Text type="secondary">None specified</Text>
                    }
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Created At">
                  <Text>{selectedRecord.created_at ? dayjs(selectedRecord.created_at).format('MMMM DD, YYYY [at] h:mm A') : 'N/A'}</Text>
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Descriptions
                column={1}
                bordered
                size="middle"
                style={{ background: 'white' }}
                labelStyle={{
                  background: designSystem.colors.neutral[50],
                  fontWeight: designSystem.typography.fontWeight.semibold,
                  color: designSystem.colors.neutral[700],
                }}
              >
                <Descriptions.Item label="Activity Type">
                  <Tag color="purple">{selectedRecord.activity_type || 'N/A'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Service Provider">
                  <Text>{selectedRecord.service_provider || 'N/A'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Location">
                  <Text><EnvironmentOutlined /> {selectedRecord.location || 'N/A'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Activity Date">
                  <Text><CalendarOutlined /> {selectedRecord.activity_date ? dayjs(selectedRecord.activity_date).format('MMMM DD, YYYY') : 'N/A'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Primary Staff">
                  <Text>{selectedRecord.primary_staff || 'N/A'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Service Delivery">
                  <Text>{selectedRecord.service_delivery || 'N/A'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Notes">
                  <Text>{selectedRecord.notes || 'No notes provided'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Documents">
                  {selectedRecord.documents?.length > 0 ? (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {selectedRecord.documents.map((doc) => (
                        <div key={doc.id} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: designSystem.spacing.sm,
                          background: designSystem.colors.neutral[50],
                          borderRadius: designSystem.borderRadius.md,
                          border: `1px solid ${designSystem.colors.neutral[200]}`,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: designSystem.spacing.sm }}>
                            <FileOutlined style={{ color: designSystem.colors.info }} />
                            <div>
                              <Text strong>{doc.description}</Text>
                              <Text style={{
                                display: 'block',
                                fontSize: designSystem.typography.fontSize.xs,
                                color: designSystem.colors.neutral[500],
                              }}>
                                Added {dayjs(doc.created_at).fromNow()}
                              </Text>
                            </div>
                          </div>
                          <Button
                            type="link"
                            icon={<DownloadOutlined />}
                            href={`/backend/${doc.file_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: designSystem.colors.info }}
                          >
                            Download
                          </Button>
                        </div>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">No documents attached</Text>
                  )}
                </Descriptions.Item>
              </Descriptions>
            )}
          </div>
        )}
      </Modal>
      {/* Boutons flottants */}
      <FloatButton.Group
        trigger="hover"
        type="primary"
        style={{ right: 24, bottom: 24 }}
        icon={<SettingOutlined />}
      >
        <FloatButton
          icon={<SyncOutlined />}
          tooltip="Refresh Data"
          onClick={handleRefresh}
        />
        <FloatButton
          icon={<FilterOutlined />}
          tooltip="Reset Filters"
          onClick={handleResetFilters}
        />
      </FloatButton.Group>
    </Layout>
  );
};

export default UserqrSupportCases;